import { Card, CardBody } from '@heroui/react';
import NextLink from 'next/link';

import {
  type CompetitorId,
  type CompetitorPricing,
  competitorPricingPath,
  describePlanPrice,
  getCompetitorPricing,
  hasPublishedPricingPage,
} from '../../data/competitor-pricing';
import { isPublishable, type SourceRef } from '../../data/pseo/types';
import { formatIsoDate } from '../pseo/LastVerifiedBadge';

export type ProofingAlternativeId = 'filestage' | 'ziflow' | 'govisually' | 'pageproof' | 'frame-io' | 'wipster';

const planPrice = (entry: CompetitorPricing, planName: string) => {
  const plan = entry.plans.find((item) => item.name === planName);
  return plan ? describePlanPrice(plan, entry.currency) : 'custom pricing';
};

/**
 * "When another tool is the better choice" notes. Competitor facts come from the verified fact sheet
 * (Oct 21, 2026) and prices from `competitor-pricing.ts`, so they stay in sync with the pricing pages.
 */
interface AlternativeNote {
  title: string;
  body: string;
  source?: SourceRef;
}

const NOTES: Record<ProofingAlternativeId, (entry: CompetitorPricing) => AlternativeNote> = {
  filestage: (entry) => ({
    title: 'You proof live websites or HTML',
    body: `Filestage proofs live websites and HTML as well as video, designs and documents, with approve or request-changes decisions, due dates and reminders. Starter is ${planPrice(entry, 'Starter')} flat for 10 seats. Kreatli has no website proofing.`,
    source: { label: 'Filestage', url: 'https://filestage.io/' },
  }),
  ziflow: (entry) => ({
    title: 'You want proofing organised into workflow stages',
    body: `Ziflow structures proofing into workflow stages (its free plan allows 1 stage). Standard is ${planPrice(entry, 'Standard')} with 15 seats included. Kreatli has custom approval statuses, but it does not route files from stage to stage automatically.`,
  }),
  govisually: (entry) => ({
    title: 'You run CPG packaging compliance',
    body: `GoVisually sells a CPG Compliance AI Suite (custom pricing) next to its Lite (${planPrice(entry, 'Lite')}) and Pro (${planPrice(entry, 'Pro')}) plans. Kreatli has no compliance, dieline or color-separation checks.`,
  }),
  pageproof: (entry) => ({
    title: 'Your team is large and you want one flat price',
    body: `PageProof Team is ${planPrice(entry, 'Team')} for unlimited users. Kreatli is priced per user, and teams above 10 members move to custom Enterprise pricing.`,
  }),
  'frame-io': () => ({
    title: 'You want review inside your editing software',
    body: 'Frame.io has a Premiere Pro panel (V4, 25.6 and later) and After Effects integration. Kreatli has no editing-software panels or plugins, and it does not export comments.',
    source: { label: 'Frame.io integrations', url: 'https://frame.io/integrations' },
  }),
  wipster: () => ({
    title: 'You want multi-step approvals with an audit trail',
    body: 'Wipster lists multi-step approvals, an audit trail and one-click sign-off, plus Premiere Pro, Final Cut Pro and After Effects panels.',
    source: { label: 'Wipster', url: 'https://wipster.io/' },
  }),
};

interface ProofingAlternativesProps {
  id?: string;
  title?: string;
  intro?: string;
  vendors: ProofingAlternativeId[];
}

/** Fairness section: when a competitor fits better than Kreatli, with sources and verified date. */
export function ProofingAlternatives({
  id = 'alternatives-heading',
  title = 'When Another Tool Is the Better Choice',
  intro = 'Kreatli is not the right fit for every proofing job. Pick another tool when one of these matters most:',
  vendors,
}: ProofingAlternativesProps) {
  const entries = vendors.map((vendor) => getCompetitorPricing(vendor as CompetitorId)).filter(isPublishable);
  if (entries.length === 0) return null;

  return (
    <section className="relative overflow-hidden px-6 py-16" aria-labelledby={id}>
      <div className="relative z-10 mx-auto max-w-4xl">
        <div className="mb-8 text-center">
          <h2 id={id} className="mb-4 font-sans text-2xl font-bold sm:text-3xl">
            {title}
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-foreground-500">{intro}</p>
        </div>
        <div className="flex flex-col gap-4">
          {entries.map((entry) => {
            const note = NOTES[entry.id as ProofingAlternativeId](entry);
            const source = note.source ?? entry.sourceUrls[0] ?? { label: entry.vendor, url: entry.vendorUrl };
            return (
              <Card key={entry.id} className="border border-foreground-200 bg-content1/60 backdrop-blur-lg">
                <CardBody className="flex flex-col gap-2 p-6">
                  <h3 className="font-sans text-lg font-semibold">{note.title}</h3>
                  <p className="text-sm text-foreground-500">{note.body}</p>
                  <p className="text-xs text-foreground-400">
                    Source:{' '}
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline underline-offset-2"
                    >
                      {source.label}
                    </a>
                    , checked {formatIsoDate(entry.lastVerified)}
                    {hasPublishedPricingPage(entry) ? (
                      <>
                        {' · '}
                        <NextLink
                          href={competitorPricingPath(entry)}
                          className="text-primary underline underline-offset-2"
                        >
                          {entry.vendor} pricing explained
                        </NextLink>
                      </>
                    ) : null}
                  </p>
                </CardBody>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
